/** «Привлечение» (docs/acquisition.md): shapes of /api/admin/v1/acquisition/* (src/api/admin/
 *  acquisition.py, src/core/acquisition/metrics.py) and the pure logic of the section — texts of
 *  metrics, flags and statuses, KPI hints, cohort cells, request paths and the campaign /
 *  channel / targets forms. No I/O, no floats: money stays a decimal string (lib/num.ts). */

import { decimalText, moneyText } from '@/lib/finance'
import { compareDecimal, parseDecimal } from '@/lib/num'

// ───────────────────────────── API shapes ─────────────────────────────

export interface AcqChannel {
  id: number
  code: string
  name: string
  /** paid | organic | referral | content | gift | broadcast | unknown */
  kind: string
  /** a system channel belongs to the attribution rules: read-only */
  is_system: boolean
  is_active: boolean
  sort_order: number
  campaigns: number
}

export interface AcqChannelsResponse {
  items: AcqChannel[]
  /** kinds a new channel may have (paid, organic, content) */
  kinds: string[]
}

export interface CampaignLinks {
  bot: string | null
  miniapp: string | null
  /** «Работает, если в BotFather настроено главное Mini App» */
  miniapp_note: string | null
  /** why links are missing (no BOT_USERNAME) or the landing card was dropped */
  note: string | null
}

export interface AcqCampaign {
  id: number
  channel_id: number
  channel_name: string | null
  code: string
  name: string
  starts_on: string | null
  ends_on: string | null
  budget_planned_rub: string | null
  pricing_model: string
  unit_price_rub: string | null
  contractor: string | null
  promo_code: string | null
  content_ref: string | null
  note: string | null
  is_active: boolean
  /** active | planned | ended | inactive (Moscow today) */
  status: string
  links: CampaignLinks
  /** users attributed so far; the code is frozen from the first one on */
  users: number
  code_locked: boolean
  /** marketing expenses linked to the campaign, all time */
  spent_rub: string | null
  created_at: string | null
  updated_at: string | null
}

export interface AcqCampaignsResponse {
  items: AcqCampaign[]
  pricing_models: string[]
  bot_username_set: boolean
  /** the page (review api-ui-8): every campaign matching the filters, and this page's window.
   *  Absent in answers of older servers (one list of at most 1000). */
  total?: number
  limit?: number
  offset?: number
}

export interface AcqFlag {
  /** cpr | activation | conversion | ltv_cac */
  key: string
  level: string
  text: string
}

/** metrics.metrics_of: money 2 decimals, percentages 1 decimal, null = division by zero. */
export interface AcqMetrics {
  spend_rub: string | null
  commission_rub: string | null
  registrations: number
  /** users of the cohort with at least one generation, to date */
  first_gen: number
  /** first_gen of registrations, 1 decimal; null without registrations */
  first_gen_pct: string | null
  activated: number
  activation_pct: string | null
  payers: number
  payers_30d: number
  conversion_pct: string | null
  conversion_30d_pct: string | null
  revenue_rub: string | null
  net_rub: string | null
  ai_cost_rub: string | null
  margin_rub: string | null
  cpr_rub: string | null
  cac_rub: string | null
  ltv_rub: string | null
  ltv30_rub: string | null
  ltv90_rub: string | null
  arppu_rub: string | null
  romi_pct: string | null
  ltv_cac: string | null
  payback_days: number | null
}

export interface AcqSummaryRow extends AcqMetrics {
  /** channel:<id> | channel:<id>:none (no campaign) | campaign:<id> */
  key: string
  label: string
  channel_id: number | null
  channel_code: string | null
  channel_name: string | null
  channel_kind: string | null
  campaign_id: number | null
  campaign_code: string | null
  flags: AcqFlag[]
}

export interface AcqTargets {
  max_cpr_rub: string
  cutoff_cpr_rub: string
  min_conversion_pct: string
  min_activation_pct: string
  min_ltv_cac: string
}

export interface AcqSummary {
  period: { date_from: string; date_to: string; tz: string; note: string }
  group: string
  channel_id: number | null
  totals: AcqMetrics & { flags: AcqFlag[] }
  rows: AcqSummaryRow[]
  /** marketing spend without a channel; null under a channel filter */
  unallocated_spend_rub: string | null
  targets: AcqTargets
  rates: { usdt_rub: string; live: boolean }
}

export interface AcqCohortCell {
  offset: number
  margin_rub: string | null
  margin_per_payer: string | null
  margin_per_user: string | null
  payers: number
}

export interface AcqCohortRow {
  /** YYYY-MM, Moscow */
  cohort: string
  registrations: number
  payers: number
  /** only the months that have started */
  cells: AcqCohortCell[]
}

export interface AcqCohorts {
  months: string[]
  offsets: number[]
  rows: AcqCohortRow[]
  channel_id: number | null
  campaign_id: number | null
  tz: string
  rates: { usdt_rub: string; live: boolean }
}

export interface AcqDay {
  date: string
  registrations: number
  spend_rub: string
}

export interface AcqTimeseries {
  days: AcqDay[]
  channel_id: number | null
  tz: string
}

export interface AcqTargetsResponse {
  targets: AcqTargets
  updated_at: string | null
  updated_by: number | null
}

/** The «Источник» block of GET /users/{id}/detail (src/core/users/admin.py). */
export interface UserAcquisition {
  channel: { id: number; code: string; name: string; kind: string }
  campaign: { id: number; code: string; name: string } | null
  first_touch_at: string | null
  /** bot | miniapp | backfill */
  via: string
  /** the raw start label: owner / admin only, else null */
  payload: string | null
  /** true when a label exists but this role may not see it */
  payload_hidden: boolean
  referrer_user_id: number | null
}

// ───────────────────────────── labels ─────────────────────────────

type Tone = 'ok' | 'warn' | 'danger' | 'neutral' | 'live'
export interface Badge {
  tone: Tone
  label: string
}

export const CHANNEL_KIND_LABEL: Record<string, string> = {
  paid: 'Платный',
  organic: 'Органика',
  referral: 'Партнёрская программа',
  content: 'Контент',
  gift: 'Подарки',
  broadcast: 'Рассылки',
  unknown: 'Неизвестная метка',
}

export function kindLabel(kind: string | null | undefined): string {
  if (!kind) return '—'
  return CHANNEL_KIND_LABEL[kind] ?? kind
}

/** Kinds whose users come by their own link formats (r_, gift_, bc_, any label): no campaigns
 *  there (mirror of NO_CAMPAIGN_KINDS in src/api/admin/acquisition.py). */
export const NO_CAMPAIGN_KINDS: readonly string[] = ['referral', 'gift', 'broadcast', 'unknown']

/** Channels a campaign may be created in or moved to: switched on and not a link-format kind.
 *  `keepId` keeps the campaign's current channel in the list even if it no longer qualifies. */
export function campaignChannels(channels: readonly AcqChannel[], keepId?: number | null): AcqChannel[] {
  return channels.filter(
    (c) => c.id === keepId || (c.is_active && !NO_CAMPAIGN_KINDS.includes(c.kind)),
  )
}

export const CAMPAIGN_STATUS: Record<string, Badge> = {
  active: { tone: 'ok', label: 'Идёт' },
  planned: { tone: 'neutral', label: 'Запланирована' },
  ended: { tone: 'neutral', label: 'Завершена' },
  inactive: { tone: 'neutral', label: 'Выключена' },
}

export function campaignStatus(status: string): Badge {
  return CAMPAIGN_STATUS[status] ?? { tone: 'neutral', label: status }
}

export const PRICING_LABEL: Record<string, string> = {
  fixed: 'Фиксированная оплата',
  cpa: 'За действие (CPA)',
  cpm: 'За 1000 показов (CPM)',
  cpc: 'За клик (CPC)',
  free: 'Бесплатно или бартер',
}
/** Models with a unit price (an estimate for the plan; the fact is the expense). */
export const UNIT_PRICE_MODELS: readonly string[] = ['cpa', 'cpm', 'cpc']

export const VIA_LABEL: Record<string, string> = {
  bot: 'Бот, команда /start',
  miniapp: 'Mini App, параметр запуска',
  backfill: 'Задним числом, по истории',
}

// ───────────────────────────── number texts ─────────────────────────────

/** "12.5" → "12,5 %", "5.0" → "5,0 %" (one decimal, like the server); null → "—" (division
 *  by zero on the server). */
export function pctText(v: string | null | undefined, unit = true): string {
  if (v === null || v === undefined || v === '') return '—'
  const t = decimalText(v, 1)
  return unit ? `${t}\u00a0%` : t
}

/** LTV/CAC "3.37" → "3,37", "3" → "3,00". */
export function ratioText(v: string | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—'
  return decimalText(v, 2)
}

/** Rubles with kopecks, digits grouped ("48 000,00"); null → "—". */
export function rubText(v: string | null | undefined): string {
  return moneyText(v, 'RUB')
}

/** A count with grouped digits: 10405 → "10 405". */
export function countText(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—'
  return moneyText(String(n), null)
}

/** True for a strictly negative decimal ("-45.90"; "-0.00" is not). */
export function isNegative(v: string | null | undefined): boolean {
  if (!v || !v.startsWith('-')) return false
  return /[1-9]/.test(v)
}

/** True for a strictly positive decimal ("0.01"; "0.00" and negatives are not). */
export function isPositive(v: string | null | undefined): boolean {
  if (!v || v.startsWith('-')) return false
  return /[1-9]/.test(v)
}

/** Russian plural: 1 день, 2 дня, 5 дней, 21 день, 11 дней. */
export function plural(n: number, one: string, few: string, many: string): string {
  const a = Math.abs(n) % 100
  const b = a % 10
  if (a > 10 && a < 20) return many
  if (b === 1) return one
  if (b >= 2 && b <= 4) return few
  return many
}

/** Payback day offset: 0 → «в первые сутки», 7 → «за 7 дней»; null → "—". */
export function paybackText(days: number | null | undefined): string {
  if (days === null || days === undefined) return '—'
  if (days === 0) return 'в первые сутки'
  return `за ${days} ${plural(days, 'день', 'дня', 'дней')}`
}

const MONTHS = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек']

/** "2026-09" → "сен 2026" (fixed table, not the runtime locale). */
export function monthLabel(key: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(key)
  if (!m) return key
  const name = MONTHS[Number(m[2]) - 1]
  return name ? `${name} ${m[1]}` : key
}

/** "2026-09-05" → "05.09.2026" (a calendar date, no time zone shift). */
export function dayText(iso: string | null | undefined): string {
  if (!iso) return '—'
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso
}

/** Tick labels of the daily chart: "05.09" within one calendar year, "05.09.26" once the
 *  period crosses a year — the summary takes up to 1100 days (review api-ui-6), and «01.01» of
 *  two years must not read the same. */
export function dailyLabels(dates: readonly string[]): string[] {
  const years = new Set(dates.map((d) => d.slice(0, 4)))
  return dates.map((d) => {
    const m = /^(\d{2})(\d{2})-(\d{2})-(\d{2})$/.exec(d)
    if (!m) return d
    return years.size > 1 ? `${m[4]}.${m[3]}.${m[2]}` : `${m[4]}.${m[3]}`
  })
}

// ───────────────────────────── Moscow days ─────────────────────────────

/** Today in Moscow as YYYY-MM-DD. Moscow is UTC+3 all year (no DST since 2014) — the backend
 *  falls back to the same fixed offset (metrics._msk). */
export function moscowToday(now: Date = new Date()): string {
  return new Date(now.getTime() + 3 * 3600 * 1000).toISOString().slice(0, 10)
}

/** The first day of the month of an ISO date. */
export function monthStart(iso: string): string {
  return `${iso.slice(0, 7)}-01`
}

/** `days` calendar days ending on `today` (inclusive). */
export function lastDaysRange(today: string, days: number): { from: string; to: string } {
  const t = new Date(`${today}T00:00:00Z`)
  t.setUTCDate(t.getUTCDate() - (days - 1))
  return { from: t.toISOString().slice(0, 10), to: today }
}

// ───────────────────────────── request paths ─────────────────────────────

export type SummaryGroup = 'channel' | 'campaign'

export interface ReportParams {
  from: string
  to: string
  group: SummaryGroup
  channelId: number | null
}

function qs(params: Record<string, string | number | null | undefined>): string {
  const q = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === '') continue
    q.set(k, String(v))
  }
  const s = q.toString()
  return s ? `?${s}` : ''
}

export function summaryPath(p: ReportParams): string {
  return `/acquisition/summary${qs({ date_from: p.from, date_to: p.to, group: p.group, channel_id: p.channelId })}`
}

export function csvPath(p: ReportParams): string {
  return `/acquisition/export.csv${qs({ date_from: p.from, date_to: p.to, group: p.group, channel_id: p.channelId })}`
}

/** The server's own file name (src/api/admin/acquisition.py export_csv). */
export function csvFileName(p: ReportParams): string {
  return `aivy-acquisition-${p.from}-${p.to}-${p.group}.csv`
}

export function timeseriesPath(p: { from: string; to: string; channelId: number | null }): string {
  return `/acquisition/timeseries${qs({ date_from: p.from, date_to: p.to, channel_id: p.channelId })}`
}

export function cohortsPath(p: { months: number; channelId: number | null; campaignId: number | null }): string {
  return `/acquisition/cohorts${qs({ months: p.months, channel_id: p.channelId, campaign_id: p.campaignId })}`
}

/** GET /acquisition/campaigns: the channel filter and, for the list, one page (review api-ui-8:
 *  `status` narrows on the server, `limit` / `offset` pick the page; the answer has `total`). */
export function campaignsPath(
  channelId: number | null,
  page?: { status?: string; limit: number; offset: number },
): string {
  return `/acquisition/campaigns${qs({
    channel_id: channelId,
    status: page?.status || null,
    limit: page?.limit,
    offset: page?.offset,
  })}`
}

/** Campaigns matching the filters: the server's `total`, else (an older server) the list itself. */
export function campaignsTotal(r: Pick<AcqCampaignsResponse, 'items' | 'total'>): number {
  return typeof r.total === 'number' ? r.total : r.items.length
}

/**
 * The rows and total of one page of «Кампании». A paginating server answers the page and its
 * `total`; its rows are still filtered by status in case it did not filter. An older server
 * ignores `limit` / `offset` and answers every row without `total`: then the filter and the
 * page are applied here, so the Pager and the table always agree.
 */
export function campaignPage(
  r: AcqCampaignsResponse,
  p: { status: string; offset: number; limit: number },
): { items: AcqCampaign[]; total: number } {
  const rows = p.status ? r.items.filter((c) => c.status === p.status) : r.items
  if (typeof r.total === 'number') return { items: rows, total: r.total }
  return { items: rows.slice(p.offset, p.offset + p.limit), total: rows.length }
}

/** Page size of `loadAllCampaigns` — the Pager's largest size, which the API accepts. */
export const CAMPAIGN_PAGE_MAX = 100
/** A guard against a runaway loop: 100 pages = 10 000 campaigns. */
const CAMPAIGN_PAGES_MAX = 100

/** Query key of the full campaign list (the selects of «Расходы», «Пользователи», «Когорты»,
 *  a campaign card opened off the current page). Under 'acq', so every acquisition change
 *  invalidates it. */
export const ALL_CAMPAIGNS_KEY = ['acq', 'campaigns', 'all'] as const

/**
 * Every campaign (of a channel) for the selects, page by page (review api-ui-8: one request
 * used to stop at 1000 and the oldest campaigns silently fell out, so their spend could not be
 * linked). The first page gives `total`; the rest are asked for together. The step is what
 * the server actually returned (it may cap `limit`). Rows are de-duplicated by id: a campaign
 * created meanwhile shifts the pages. An older server without `total` is one request.
 */
export async function loadAllCampaigns(
  get: (path: string) => Promise<AcqCampaignsResponse>,
  channelId: number | null,
): Promise<AcqCampaignsResponse> {
  const first = await get(campaignsPath(channelId, { limit: CAMPAIGN_PAGE_MAX, offset: 0 }))
  const step = first.items.length
  if (typeof first.total !== 'number' || step === 0 || step >= first.total) {
    return { ...first, total: campaignsTotal(first) }
  }
  const offsets: number[] = []
  for (let o = step; o < first.total && offsets.length < CAMPAIGN_PAGES_MAX; o += step) offsets.push(o)
  const rest = await Promise.all(offsets.map((offset) => get(campaignsPath(channelId, { limit: CAMPAIGN_PAGE_MAX, offset }))))
  const byId = new Map<number, AcqCampaign>()
  for (const page of [first, ...rest]) for (const c of page.items) if (!byId.has(c.id)) byId.set(c.id, c)
  return { ...first, items: [...byId.values()], total: first.total, limit: byId.size, offset: 0 }
}

/** The link a QR image of `target` encodes; null when the campaign has none. */
export function qrLink(links: CampaignLinks, target: LinkTarget): string | null {
  return target === 'bot' ? links.bot : links.miniapp
}

/** A short, stable tag of a link (32-bit FNV-1a, base 36): the QR request's version. */
export function linkVersion(link: string): string {
  let h = 0x811c9dc5
  for (let i = 0; i < link.length; i++) {
    h ^= link.charCodeAt(i)
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return h.toString(36)
}

/** GET …/qr.svg. With the link it encodes, the URL carries its version (review api-ui-3): the
 *  SVG is cached for 5 minutes (`private, max-age=300`), so after a code or landing change the
 *  same URL would give the old image — a new link is a new URL, the same link keeps its cache. */
export function qrPath(campaignId: number, target: LinkTarget, link?: string | null): string {
  return `/acquisition/campaigns/${campaignId}/qr.svg${qs({ target, v: link ? linkVersion(link) : null })}`
}

/** GET /users with the first-touch filters (docs/acquisition.md §5 «Пользователи»). */
export function usersListPath(p: {
  limit: number
  offset: number
  search?: string
  channelId?: number | null
  campaignId?: number | null
  /** the backend's `tier` / `banned` filters (users.py list_users) */
  tier?: string | null
  banned?: boolean | null
}): string {
  return `/users${qs({
    limit: p.limit,
    offset: p.offset,
    search: p.search?.trim() || null,
    tier: p.tier || null,
    banned: p.banned === undefined || p.banned === null ? null : String(p.banned),
    acq_channel_id: p.channelId ?? null,
    acq_campaign_id: p.campaignId ?? null,
  })}`
}

/** A positive integer id from a query-string value, else null. */
export function idParam(raw: string | null | undefined): number | null {
  if (!raw || !/^\d{1,15}$/.test(raw)) return null
  const n = Number(raw)
  return n > 0 ? n : null
}

const ACQ_URL_FILTERS = ['acq_channel_id', 'acq_campaign_id'] as const

/**
 * The first-touch filters of «Пользователи» from its URL (?acq_channel_id= / ?acq_campaign_id=,
 * links from «Привлечение»). Only a role with acquisition.view gets them (review api-ui-10):
 * support opening a shared link would otherwise see a silently filtered list with no control
 * to clear it. For such a role `cleaned` is the URL without them (null = nothing to remove);
 * the input is not changed.
 */
export function acqUrlFilters(
  params: URLSearchParams,
  canAcq: boolean,
): { channelId: number | null; campaignId: number | null; cleaned: URLSearchParams | null } {
  if (canAcq) {
    return {
      channelId: idParam(params.get('acq_channel_id')),
      campaignId: idParam(params.get('acq_campaign_id')),
      cleaned: null,
    }
  }
  if (!ACQ_URL_FILTERS.some((k) => params.has(k))) return { channelId: null, campaignId: null, cleaned: null }
  const cleaned = new URLSearchParams(params)
  for (const k of ACQ_URL_FILTERS) cleaned.delete(k)
  return { channelId: null, campaignId: null, cleaned }
}

// ───────────────────────────── flags and KPI ─────────────────────────────

/** A server flag as a badge: warn → warn, danger → danger, the server's Russian text. */
export function flagBadge(flag: AcqFlag): Badge {
  return { tone: flag.level === 'danger' ? 'danger' : 'warn', label: flag.text }
}

/** What the «сигналы» cell of a row says: its flags; «В цели» when at least one metric that has
 *  a target was measured and none is off; nothing when no such metric exists (a spend-only
 *  row, a row without registrations). */
export type Verdict = { kind: 'flags'; badges: Badge[] } | { kind: 'ok' } | { kind: 'none' }

export function rowVerdict(row: AcqMetrics & { flags: AcqFlag[] }): Verdict {
  if (row.flags.length > 0) {
    // danger first: the worst signal leads
    const sorted = [...row.flags].sort((a, b) => Number(b.level === 'danger') - Number(a.level === 'danger'))
    return { kind: 'flags', badges: sorted.map(flagBadge) }
  }
  const measured = [row.cpr_rub, row.activation_pct, row.conversion_pct, row.ltv_cac].some((v) => v !== null)
  return measured ? { kind: 'ok' } : { kind: 'none' }
}

export interface KpiCard {
  key: string
  label: string
  value: string
  hints: string[]
  badge: Badge | null
}

function flagOf(flags: AcqFlag[], key: string): AcqFlag | undefined {
  return flags.find((f) => f.key === key)
}

function trimZeros(v: string): string {
  return moneyText(v, null)
}

/** A number and its unit never part at a line end: «45 ₽», «4,5 %» with a no-break space. */
const U = '\u00a0'

/** The six KPI tiles of «Сводка» (Расход, Регистрации, CAC, Конверсия, LTV/CAC, ROMI) with the
 *  owner's targets as hints. The badges follow the server's flags of the totals — the client
 *  never re-decides a flag. */
export function kpiCards(
  totals: AcqMetrics & { flags: AcqFlag[] },
  targets: AcqTargets,
  unallocated: string | null,
): KpiCard[] {
  const spendHints: string[] = []
  if (isPositive(totals.commission_rub))
    spendHints.push(`в том числе комиссии партнёрам ${rubText(totals.commission_rub)}${U}₽`)
  if (isPositive(unallocated))
    spendHints.push(`не распределено по каналам ${rubText(unallocated)}${U}₽`)

  const cpr = flagOf(totals.flags, 'cpr')
  const conv = flagOf(totals.flags, 'conversion')
  const act = flagOf(totals.flags, 'activation')
  const ltvCac = flagOf(totals.flags, 'ltv_cac')

  const regBadge: Badge | null = cpr
    ? { tone: cpr.level === 'danger' ? 'danger' : 'warn', label: cpr.level === 'danger' ? 'Цена выше отсечки' : 'Цена выше цели' }
    : totals.cpr_rub !== null
      ? { tone: 'ok', label: 'Цена в цели' }
      : null

  const romiBadge: Badge | null =
    totals.romi_pct === null
      ? null
      : isNegative(totals.romi_pct)
        ? { tone: 'neutral', label: 'Пока не окупилось' }
        : { tone: 'ok', label: 'Окупилось' }

  return [
    { key: 'spend', label: 'Расход, ₽', value: rubText(totals.spend_rub), hints: spendHints, badge: null },
    {
      key: 'registrations',
      label: 'Регистрации',
      value: countText(totals.registrations),
      hints: [
        `цена регистрации ${totals.cpr_rub === null ? '—' : `${rubText(totals.cpr_rub)}${U}₽`}, цель до ${trimZeros(targets.max_cpr_rub)}${U}₽, отсечка ${trimZeros(targets.cutoff_cpr_rub)}${U}₽`,
        `активация ${pctText(totals.activation_pct)}, цель от ${trimZeros(targets.min_activation_pct)}${U}%${act ? ' — ниже цели' : ''}`,
      ],
      badge: regBadge,
    },
    {
      key: 'cac',
      label: 'CAC, ₽',
      value: rubText(totals.cac_rub),
      hints: [
        `платящих ${countText(totals.payers)}`,
        `LTV ${totals.ltv_rub === null ? '—' : `${rubText(totals.ltv_rub)}${U}₽`} на платящего`,
      ],
      badge: null,
    },
    {
      key: 'conversion',
      label: 'Конверсия в оплату',
      value: pctText(totals.conversion_pct),
      hints: [`за 30 дней ${pctText(totals.conversion_30d_pct)}`, `цель от ${trimZeros(targets.min_conversion_pct)}${U}%`],
      badge: conv ? { tone: 'warn', label: 'Ниже цели' } : totals.conversion_pct !== null ? { tone: 'ok', label: 'В цели' } : null,
    },
    {
      key: 'ltv_cac',
      label: 'LTV/CAC',
      value: ratioText(totals.ltv_cac),
      hints: [
        `цель от ${trimZeros(targets.min_ltv_cac)}`,
        ...(totals.ltv_cac === null ? ['нет расхода или платящих'] : []),
      ],
      badge: ltvCac
        ? { tone: ltvCac.level === 'danger' ? 'danger' : 'warn', label: 'Ниже цели' }
        : totals.ltv_cac !== null
          ? { tone: 'ok', label: 'В цели' }
          : null,
    },
    {
      key: 'romi',
      label: 'ROMI',
      value: pctText(totals.romi_pct),
      hints: [
        `маржа ${totals.margin_rub === null ? '—' : `${rubText(totals.margin_rub)}${U}₽`}`,
        totals.payback_days === null
          ? totals.romi_pct === null
            ? 'расхода нет'
            : 'маржа ещё не догнала расход'
          : `окупилось ${paybackText(totals.payback_days)}`,
      ],
      badge: romiBadge,
    },
  ]
}

/** Campaign rows of a group=campaign report under their channel (the expanded channel rows). */
export function childrenByChannel(rows: readonly AcqSummaryRow[]): Map<number, AcqSummaryRow[]> {
  const out = new Map<number, AcqSummaryRow[]>()
  for (const r of rows) {
    if (r.channel_id === null) continue
    const list = out.get(r.channel_id) ?? []
    list.push(r)
    out.set(r.channel_id, list)
  }
  return out
}

/** Second line under a row's label: the channel and code of a campaign row, or what a
 *  «Без кампании» row holds. */
export function rowCaption(row: AcqSummaryRow, group: string): string | null {
  if (row.campaign_id !== null) {
    const code = row.campaign_code ? `код ${row.campaign_code}` : null
    return group === 'campaign' ? [row.channel_name, code].filter(Boolean).join(', ') : code
  }
  if (row.key.endsWith(':none')) {
    return group === 'campaign' ? `${row.channel_name ?? 'Канал'}: пользователи и расходы без кампании` : 'пользователи и расходы без кампании'
  }
  return null
}

// ───────────────────────────── cohorts ─────────────────────────────

export type CohortMode = 'payer' | 'user' | 'payers'

export const COHORT_MODE_LABEL: Record<CohortMode, string> = {
  payer: 'Маржа на платящего, ₽',
  user: 'Маржа на зарегистрированного, ₽',
  payers: 'Платящих к концу месяца',
}

/** How a cohort cell looks: `none` (no payers yet), `neg` (AI cost still above net revenue —
 *  danger ink with the minus sign), `plain`, `ok` (margin per registered user reached the
 *  owner's registration-price target: the cohort paid back its target acquisition price —
 *  ok-soft background, a ✓ and the word in the tooltip; no new colours, design §6.7). */
export type CohortTone = 'none' | 'neg' | 'plain' | 'ok'

export interface CohortCellView {
  text: string
  title: string
  tone: CohortTone
}

export function cohortCell(
  cell: AcqCohortCell,
  registrations: number,
  mode: CohortMode,
  targetCpr: string | null,
): CohortCellView {
  const perUser = cell.margin_per_user
  let tone: CohortTone = 'plain'
  if (cell.margin_per_payer === null && cell.payers === 0) tone = isNegative(perUser) ? 'neg' : 'none'
  else if (isNegative(cell.margin_per_payer) || isNegative(perUser)) tone = 'neg'
  else if (targetCpr !== null && perUser !== null && compareDecimal(perUser, targetCpr) >= 0) tone = 'ok'

  const money = mode === 'payer' ? cell.margin_per_payer : perUser
  const text = mode === 'payers' ? countText(cell.payers) : money === null ? '—' : rubText(money)
  const parts = [
    `Маржа на платящего: ${cell.margin_per_payer === null ? 'платящих нет' : `${rubText(cell.margin_per_payer)}${U}₽`}`,
    `на зарегистрированного: ${perUser === null ? '—' : `${rubText(perUser)}${U}₽`}`,
    `заплатили ${countText(cell.payers)} из ${countText(registrations)}`,
  ]
  if (tone === 'ok' && targetCpr !== null) parts.push(`окупила цель по цене регистрации ${trimZeros(targetCpr)}${U}₽`)
  if (tone === 'neg') parts.push('генерации пока стоят больше, чем принесли оплаты')
  return { text, title: parts.join('; '), tone }
}

// ───────────────────────────── links ─────────────────────────────

export type LinkTarget = 'bot' | 'miniapp'

export interface LinkItem {
  target: LinkTarget
  label: string
  url: string
  /** the start parameter and its length (Telegram allows 64) */
  param: string
  length: number
  note: string | null
}

/** The start / startapp parameter of a t.me link. */
export function startParam(url: string): string {
  const m = /[?&](?:start|startapp)=([^&#]*)/.exec(url)
  return m ? decodeURIComponent(m[1] ?? '') : ''
}

/** The links of a campaign to show and copy: the bot link first (it works everywhere), then the
 *  Mini App link with its BotFather note. Missing links are left out (no bot username). */
export function linkItems(links: CampaignLinks): LinkItem[] {
  const out: LinkItem[] = []
  if (links.bot) {
    const p = startParam(links.bot)
    out.push({ target: 'bot', label: 'Ссылка на бота', url: links.bot, param: p, length: p.length, note: null })
  }
  if (links.miniapp) {
    const p = startParam(links.miniapp)
    out.push({
      target: 'miniapp',
      label: 'Ссылка на Mini App',
      url: links.miniapp,
      param: p,
      length: p.length,
      note: links.miniapp_note,
    })
  }
  return out
}

export interface ClipboardDeps {
  writeText?: (text: string) => Promise<void>
  /** the old path: select a hidden field and run the copy command */
  fallback?: (text: string) => boolean
}

/** Copy `text`: the async Clipboard API, else (no API, insecure origin, denied) the fallback.
 *  Resolves true when one of them worked. Never throws. */
export async function copyText(text: string, deps: ClipboardDeps): Promise<boolean> {
  if (deps.writeText) {
    try {
      await deps.writeText(text)
      return true
    } catch {
      /* permission denied or not a secure context: try the fallback */
    }
  }
  try {
    return deps.fallback ? deps.fallback(text) : false
  } catch {
    return false
  }
}

// ───────────────────────────── campaign form ─────────────────────────────

export const CAMPAIGN_CODE_RE = /^[a-z0-9]{3,16}$/
const CONTENT_REF_RE = /^(t\d{1,18}|[A-Za-z0-9_-]{1,57})$/
const NAME_MAX = 200
const TEXT_MAX = 2000
const MONEY_2DP = /^\d{1,16}(\.\d{1,2})?$/

export interface CampaignDraft {
  channel_id: string
  code: string
  name: string
  starts_on: string
  ends_on: string
  budget_planned_rub: string
  pricing_model: string
  unit_price_rub: string
  contractor: string
  promo_code: string
  content_ref: string
  note: string
  is_active: boolean
}

export type CampaignField = keyof CampaignDraft

/** A money figure for an input: "60000.00" → "60000", "182.50" → "182.5". */
function moneyInput(v: string | null): string {
  if (v === null) return ''
  return v.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '')
}

export function campaignDraft(c: AcqCampaign | null, channelId: number | null = null): CampaignDraft {
  return {
    channel_id: c ? String(c.channel_id) : channelId === null ? '' : String(channelId),
    code: c?.code ?? '',
    name: c?.name ?? '',
    starts_on: c?.starts_on ?? '',
    ends_on: c?.ends_on ?? '',
    budget_planned_rub: moneyInput(c?.budget_planned_rub ?? null),
    pricing_model: c?.pricing_model ?? 'fixed',
    unit_price_rub: moneyInput(c?.unit_price_rub ?? null),
    contractor: c?.contractor ?? '',
    promo_code: c?.promo_code ?? '',
    content_ref: c?.content_ref ?? '',
    note: c?.note ?? '',
    is_active: c?.is_active ?? true,
  }
}

/** The channel is frozen once the campaign has users or expenses (the server refuses the move
 *  until the owner decides otherwise — src/api/admin/acquisition.py update_campaign). */
export function channelLocked(c: AcqCampaign | null): boolean {
  if (c === null) return false
  return c.users > 0 || isPositive(c.spent_rub)
}

function money(raw: string, label: string, errors: Partial<Record<CampaignField, string>>, key: CampaignField): string | null {
  const v = parseDecimal(raw)
  if (v === null) return null
  if (v === undefined || !MONEY_2DP.test(v)) {
    errors[key] = `${label}: число не меньше нуля, до двух знаков после запятой`
    return null
  }
  return v
}

export type CampaignResult =
  | { ok: true; body: Record<string, unknown> }
  | { ok: false; errors: Partial<Record<CampaignField, string>> }

/** The POST body of a new campaign, or the PATCH body of the fields that changed (an unchanged
 *  code or channel is never sent: they may be locked). Messages mirror the server's. */
export function campaignBody(d: CampaignDraft, original: AcqCampaign | null): CampaignResult {
  const errors: Partial<Record<CampaignField, string>> = {}
  const channelId = /^\d+$/.test(d.channel_id) ? Number(d.channel_id) : null
  if (channelId === null) errors.channel_id = 'Выберите канал'
  const code = d.code.trim()
  if (!CAMPAIGN_CODE_RE.test(code)) errors.code = 'От 3 до 16 строчных латинских букв и цифр, без _ и -'
  const name = d.name.trim()
  if (!name) errors.name = 'Название: обязательное поле'
  else if (name.length > NAME_MAX) errors.name = `Название: не длиннее ${NAME_MAX} символов`
  const dateRe = /^\d{4}-\d{2}-\d{2}$/
  if (d.starts_on && !dateRe.test(d.starts_on)) errors.starts_on = 'Дата в формате ГГГГ-ММ-ДД'
  if (d.ends_on && !dateRe.test(d.ends_on)) errors.ends_on = 'Дата в формате ГГГГ-ММ-ДД'
  if (!errors.starts_on && !errors.ends_on && d.starts_on && d.ends_on && d.ends_on < d.starts_on)
    errors.ends_on = 'Дата окончания раньше даты начала'
  const budget = money(d.budget_planned_rub, 'Плановый бюджет', errors, 'budget_planned_rub')
  const unitModel = UNIT_PRICE_MODELS.includes(d.pricing_model)
  const unit = unitModel ? money(d.unit_price_rub, 'Цена за единицу', errors, 'unit_price_rub') : null
  const contractor = d.contractor.trim()
  if (contractor.length > NAME_MAX) errors.contractor = `Подрядчик: не длиннее ${NAME_MAX} символов`
  const contentRef = d.content_ref.trim()
  if (contentRef && !CONTENT_REF_RE.test(contentRef))
    errors.content_ref = 't<id> тренда, id или slug эффекта: латиница, цифры, _ и -'
  const note = d.note.trim()
  if (note.length > TEXT_MAX) errors.note = `Заметка: не длиннее ${TEXT_MAX} символов`
  if (Object.keys(errors).length > 0) return { ok: false, errors }

  const full: Record<string, unknown> = {
    channel_id: channelId,
    code,
    name,
    starts_on: d.starts_on || null,
    ends_on: d.ends_on || null,
    budget_planned_rub: budget,
    pricing_model: d.pricing_model,
    unit_price_rub: unit,
    contractor: contractor || null,
    promo_code: d.promo_code.trim() || null,
    content_ref: contentRef || null,
    note: note || null,
    is_active: d.is_active,
  }
  if (original === null) return { ok: true, body: full }

  const before = campaignBodyOf(original)
  const body: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(full)) {
    if (!sameValue(before[k], v)) body[k] = v
  }
  return { ok: true, body }
}

/** The stored campaign in the body's terms (for the PATCH diff). */
function campaignBodyOf(c: AcqCampaign): Record<string, unknown> {
  return {
    channel_id: c.channel_id,
    code: c.code,
    name: c.name,
    starts_on: c.starts_on,
    ends_on: c.ends_on,
    budget_planned_rub: c.budget_planned_rub,
    pricing_model: c.pricing_model,
    unit_price_rub: c.unit_price_rub,
    contractor: c.contractor,
    promo_code: c.promo_code,
    content_ref: c.content_ref,
    note: c.note,
    is_active: c.is_active,
  }
}

function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (typeof a === 'string' && typeof b === 'string' && /^\d+(\.\d+)?$/.test(a) && /^\d+(\.\d+)?$/.test(b))
    return compareDecimal(a, b) === 0 // "60000.00" (stored) vs "60000" (typed)
  return false
}

/** Spent vs the planned budget in rubles, without the unit — the column header names it
 *  («Расход и бюджет, ₽»; wave-4 review: the cell's own «₽» made «… ₽ ₽» on a phone):
 *  "48 000,00 из 60 000,00", or "0,00" with the note «бюджет не задан»; and whether it is over. */
export function budgetText(c: Pick<AcqCampaign, 'budget_planned_rub' | 'spent_rub'>): {
  text: string
  note: string | null
  over: boolean
} {
  const spent = c.spent_rub ?? '0'
  if (c.budget_planned_rub === null) return { text: rubText(spent), note: 'бюджет не задан', over: false }
  return {
    text: `${rubText(spent)} из ${rubText(c.budget_planned_rub)}`,
    note: null,
    over: !spent.startsWith('-') && compareDecimal(spent, c.budget_planned_rub) > 0,
  }
}

/** Campaign period in words: «с 05.09.2026 по 30.09.2026», «с 02.09.2026», «без дат». */
export function periodText(c: Pick<AcqCampaign, 'starts_on' | 'ends_on'>): string {
  if (!c.starts_on && !c.ends_on) return 'без дат'
  const parts: string[] = []
  if (c.starts_on) parts.push(`с ${dayText(c.starts_on)}`)
  if (c.ends_on) parts.push(`по ${dayText(c.ends_on)}`)
  return parts.join(' ')
}

// ───────────────────────────── channel form ─────────────────────────────

export const CHANNEL_CODE_RE = /^[a-z0-9_]{2,32}$/
export const NEW_CHANNEL_KINDS: readonly string[] = ['paid', 'organic', 'content']

export interface ChannelDraft {
  code: string
  name: string
  kind: string
  sort_order: string
}

export type ChannelResult =
  | { ok: true; body: { code: string; name: string; kind: string; sort_order: number } }
  | { ok: false; errors: Partial<Record<keyof ChannelDraft, string>> }

export function channelBody(d: ChannelDraft): ChannelResult {
  const errors: Partial<Record<keyof ChannelDraft, string>> = {}
  const code = d.code.trim()
  if (!CHANNEL_CODE_RE.test(code)) errors.code = 'От 2 до 32 строчных латинских букв, цифр и _'
  const name = d.name.trim()
  if (!name) errors.name = 'Название: обязательное поле'
  else if (name.length > NAME_MAX) errors.name = `Название: не длиннее ${NAME_MAX} символов`
  if (!NEW_CHANNEL_KINDS.includes(d.kind)) errors.kind = 'Платный, органика или контент'
  const sort = sortOrder(d.sort_order)
  if (sort === null) errors.sort_order = 'Порядок: целое число от 0 до 100000'
  if (Object.keys(errors).length > 0 || sort === null) return { ok: false, errors }
  return { ok: true, body: { code, name, kind: d.kind, sort_order: sort } }
}

export type ChannelSubmit =
  | { kind: 'invalid'; errors: Partial<Record<keyof ChannelDraft, string>> }
  | { kind: 'refused'; message: string }
  | { kind: 'created' }

/**
 * «Создать канал» (review api-ui-9): check the draft, then wait for the server. `create`
 * resolves null when the channel was created, else the server's Russian message (a taken code,
 * a bad kind); a rejection counts as a refusal too. Only `created` may clear and close the form —
 * a refusal keeps what the admin typed and shows why.
 */
export async function submitChannel(
  d: ChannelDraft,
  create: (body: { code: string; name: string; kind: string; sort_order: number }) => Promise<string | null>,
): Promise<ChannelSubmit> {
  const res = channelBody(d)
  if (!res.ok) return { kind: 'invalid', errors: res.errors }
  try {
    const refused = await create(res.body)
    return refused === null ? { kind: 'created' } : { kind: 'refused', message: refused }
  } catch (e) {
    return { kind: 'refused', message: e instanceof Error && e.message ? e.message : 'ошибка сети' }
  }
}

/** "100" → 100; out of 0..100000 or not a whole number → null. */
export function sortOrder(raw: string): number | null {
  const s = raw.replace(/\s/g, '')
  if (!/^\d{1,6}$/.test(s)) return null
  const n = Number(s)
  return n <= 100_000 ? n : null
}

// ───────────────────────────── targets form ─────────────────────────────

export type TargetKey = keyof AcqTargets

export interface TargetField {
  key: TargetKey
  label: string
  unit: string
  help: string
}

/** Labels mirror metrics.TARGET_LABELS; the order follows the funnel. */
export const TARGET_FIELDS: readonly TargetField[] = [
  { key: 'max_cpr_rub', label: 'Цель по цене регистрации', unit: '₽', help: 'Дороже — флаг «Цена регистрации выше цели»' },
  { key: 'cutoff_cpr_rub', label: 'Отсечка по цене регистрации', unit: '₽', help: 'Дороже — флаг «выше отсечки»: канал стоит остановить. Не ниже цели' },
  { key: 'min_activation_pct', label: 'Минимальная активация', unit: '%', help: 'Доля тех, кто сделал генерацию в первые 7 суток' },
  { key: 'min_conversion_pct', label: 'Минимальная конверсия в оплату', unit: '%', help: 'Доля платящих от регистраций, за всё время' },
  { key: 'min_ltv_cac', label: 'Минимальное LTV/CAC', unit: '', help: 'Ниже — флаг «LTV/CAC ниже цели»: канал не окупает привлечение' },
]

const PCT_KEYS: readonly TargetKey[] = ['min_conversion_pct', 'min_activation_pct']
const TARGET_MAX = '1000000000'

export type TargetsDraft = Record<TargetKey, string>

export type TargetsResult =
  | { ok: true; body: { targets: Partial<Record<TargetKey, string>> } }
  | { ok: false; errors: Partial<Record<TargetKey, string>> }

export function targetsDraft(t: AcqTargets): TargetsDraft {
  return { ...t }
}

/** The PUT /targets body: only the targets that changed (a partial update), or the errors —
 *  the server's rules: a number ≥ 0, percentages ≤ 100, the cutoff not below the target. */
export function targetsBody(draft: TargetsDraft, current: AcqTargets): TargetsResult {
  const errors: Partial<Record<TargetKey, string>> = {}
  const parsed: Partial<Record<TargetKey, string>> = {}
  for (const f of TARGET_FIELDS) {
    const v = parseDecimal(draft[f.key])
    if (v === null || v === undefined) {
      errors[f.key] = 'Нужно число не меньше нуля, например 45 или 4,5'
      continue
    }
    if (compareDecimal(v, TARGET_MAX) > 0) errors[f.key] = 'Слишком большое число'
    else if (PCT_KEYS.includes(f.key) && compareDecimal(v, '100') > 0) errors[f.key] = 'Не больше 100 %'
    else parsed[f.key] = v
  }
  const max = parsed.max_cpr_rub
  const cutoff = parsed.cutoff_cpr_rub
  if (max !== undefined && cutoff !== undefined && compareDecimal(cutoff, max) < 0)
    errors.cutoff_cpr_rub = 'Отсечка не может быть ниже цели по цене регистрации'
  if (Object.keys(errors).length > 0) return { ok: false, errors }
  const targets: Partial<Record<TargetKey, string>> = {}
  for (const f of TARGET_FIELDS) {
    const v = parsed[f.key]
    if (v !== undefined && compareDecimal(v, current[f.key]) !== 0) targets[f.key] = v
  }
  return { ok: true, body: { targets } }
}
